import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import BasicDialog from "../../../common/components/functional/dialogs/BasicDialog";
import { CustomerOption } from "../../../common/components/functional/fields/customerAutocompleteField/useCustomerAutocomplete";
import { WorkLocationOption } from "../../../common/components/functional/fields/workLocationAutocompleteField/useWorkLocationAutocomplete";
import IconWrapper from "../../../common/components/functional/icons/IconWrapper";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { StripedTable } from "../../../common/components/functional/stripedTable/StripedTable";
import { isTalentActive } from "../../../common/helpers/common";
import { leadingZerosDateFormat } from "../../../common/helpers/formatDateTime";
import isEmpty from "../../../common/helpers/isEmpty";
import { ReactComponent as DeleteIcon } from "../../../common/icons/deleteDark.svg";
import { ReactComponent as EditIcon } from "../../../common/icons/editDark.svg";
import { ReactComponent as InfoWarningIcon } from "../../../common/icons/info-warning.svg";
import { TempEmploymentReasonCode } from "../../../common/types/TempEmploymentReasonCode";
import { fetchCustomerOrgById } from "../../customer/customerOrganizationSlice";
import { fetchWorkLocationById } from "../../customer/customerWorkLocationsSlice";
import {
  addSelfServiceOrderPermission,
  fetchTalentSelfServiceOrderPermissions,
  removeSelfServiceOrderPermission,
  selectTalentStatusById,
  updateSelfServiceOrderPermission,
} from "../../talents/talentsSlice";
import { TalentSelfServiceOrderPermission } from "../../talents/types/queries";
import {
  getValidityEndDate,
  isIndefiniteDate,
  sortPermissions,
} from "./helpers";
import SelfServicePermissionForm from "./SelfServicePermissionForm";
import { TalentSelfServiceOrderPermissionForm } from "./types";

const TalentSelfServiceOrderPermissions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] =
    useState<TalentSelfServiceOrderPermission | null>(null);
  const [permissionToDelete, setPermissionToDelete] =
    useState<TalentSelfServiceOrderPermission | null>(null);
  const [
    talentSelfServiceOrderPermissions,
    setTalentSelfServiceOrderPermissions,
  ] = useState<TalentSelfServiceOrderPermission[]>([]);
  const isEditing = !!editingPermission;

  const { talentId } = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const talentStatus = useAppSelector((state) =>
    selectTalentStatusById(state, talentId),
  );
  const isActiveTalent = isTalentActive(talentStatus);

  const defaultValues: TalentSelfServiceOrderPermissionForm = useMemo(
    () => ({
      customerOrganization: null,
      workLocation: null,
      orderRowTemplate: null,
      customerCostCenter: null,
      reasonCode: null,
      permissionStart: null,
      permissionEnd: null,
      indefinite: false,
    }),
    [],
  );

  const methods = useForm<TalentSelfServiceOrderPermissionForm>({
    defaultValues,
    mode: "onChange",
  });

  const {
    watch,
    formState: { isDirty, isValid, errors },
    setValue,
    clearErrors,
    trigger,
    reset,
    handleSubmit,
  } = methods;

  const customerOrganization = watch("customerOrganization");
  const workLocation = watch("workLocation");
  const endDate = watch("permissionEnd");
  const indefinite = watch("indefinite");
  const startDate = watch("permissionStart");

  // Clear errors when date fields change
  useEffect(() => {
    clearErrors("permissionStart");
    clearErrors("permissionEnd");
    clearErrors("indefinite");
  }, [startDate, endDate, indefinite, clearErrors]);

  useEffect(() => {
    if (!editingPermission) {
      reset(defaultValues);
      return;
    }

    reset({
      customerOrganization: {
        id: editingPermission.customerOrganizationId,
        name: editingPermission.customerOrganizationName,
      },
      workLocation: {
        id: editingPermission.workLocationId,
        name: editingPermission.workLocationName,
        customerOrgId: editingPermission.customerOrganizationId,
        customerOrgName: editingPermission.customerOrganizationName,
        active: true,
        contractId: "",
      },
      orderRowTemplate: {
        id: editingPermission.orderRowTemplateId,
        name: editingPermission.orderRowTemplateName,
        workTitleId: editingPermission.workTitleId,
        active: true,
      },
      customerCostCenter: {
        id: editingPermission.customerCostCenterId,
        name: editingPermission.customerCostCenterName,
        customerOrgId: editingPermission.customerOrganizationId,
        active: true,
      },
      reasonCode: editingPermission.reasonCode as TempEmploymentReasonCode,
      permissionStart: parseISO(editingPermission.permissionStart),
      permissionEnd: isIndefiniteDate(editingPermission.permissionEnd)
        ? null
        : parseISO(editingPermission.permissionEnd),
      indefinite: isIndefiniteDate(editingPermission.permissionEnd),
    });
  }, [editingPermission, methods, defaultValues, reset]);

  const resetOnCustomerOrganizationChange = (org: CustomerOption | null) => {
    setValue("customerOrganization", org, { shouldDirty: true });

    if (org) {
      // Empty dependent fields when field is filled
      clearErrors("customerOrganization");
      setValue("workLocation", null, { shouldDirty: true });
      setValue("orderRowTemplate", null, { shouldDirty: true });
      setValue("customerCostCenter", null, { shouldDirty: true });
    } else {
      // Empty dependent fields when field is filled and trigger validation
      setValue("workLocation", null, { shouldDirty: true });
      setValue("orderRowTemplate", null, { shouldDirty: true });
      setValue("customerCostCenter", null, { shouldDirty: true });
      void trigger([
        "workLocation",
        "orderRowTemplate",
        "customerCostCenter",
        "customerOrganization",
      ]);
    }
  };

  const resetOnWorkLocationChange = (wl: WorkLocationOption | null) => {
    setValue("workLocation", wl, { shouldDirty: true });

    if (wl) {
      // Empty dependent fields when field is filled
      clearErrors("workLocation");
      setValue("orderRowTemplate", null, { shouldDirty: true });
    } else {
      // Empty dependent fields when field is filled and trigger validation
      setValue("orderRowTemplate", null, { shouldDirty: true });
      void trigger(["workLocation", "orderRowTemplate"]);
    }
  };

  const headers = [
    t("common:customerCompany"),
    t("order:workLocation"),
    t("order:orderTemplate"),
    t("order:customerCostCenter"),
    t("order:reasonForTemporaryEmployment"),
    t("order:dateFrom"),
    t("order:dateTo"),
    "",
  ];

  useEffect(() => {
    if (!talentId) return;
    setIsLoading(true);
    dispatch(fetchTalentSelfServiceOrderPermissions({ talentId: talentId!! }))
      .then((action) => {
        if (isFulfilled(action)) {
          setTalentSelfServiceOrderPermissions(action.payload);
        }
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, talentId]);

  const onAdd = () => {
    setEditingPermission(null);
    reset(defaultValues);
    setDialogOpen(true);
  };

  const onEdit = async (id: string) => {
    const permission = talentSelfServiceOrderPermissions.find(
      (p) => p.id === id,
    );
    if (!permission) return;

    setIsLoading(true);
    setEditingPermission(permission);
    setDialogOpen(true);

    const orgAction = await dispatch(
      fetchCustomerOrgById({ id: permission.customerOrganizationId }),
    );

    const workLocationAction = await dispatch(
      fetchWorkLocationById({ workLocationId: permission.workLocationId }),
    );

    if (isFulfilled(orgAction) && isFulfilled(workLocationAction)) {
      const orgDetails = orgAction.payload;
      const workLocationDetails = workLocationAction.payload;

      // enrich data to the form
      setValue(
        "customerOrganization",
        {
          id: orgDetails.id,
          name: orgDetails.name,
          customerCompanyId: orgDetails.customerCompanyId,
        },
        { shouldDirty: false },
      );

      setValue(
        "workLocation",
        {
          id: workLocationDetails.workLocationId,
          name: workLocationDetails.name,
          customerOrgId: workLocationDetails.customerOrgId,
          customerOrgName: workLocationDetails.customerOrgName,
          active: workLocationDetails.active,
          contractId: workLocationDetails.contractId,
        },
        { shouldDirty: false },
      );

      setIsLoading(false);
    }
    setIsLoading(false);
  };

  const onDelete = (id: string) => {
    const permission = talentSelfServiceOrderPermissions.find(
      (p) => p.id === id,
    );
    if (!permission) return;

    setPermissionToDelete(permission);
    setConfirmationDialogOpen(true);
  };

  const resetDeleteState = () => {
    setConfirmationDialogOpen(false);
    setPermissionToDelete(null);
  };

  const onDeleteConfirm = () => {
    if (!permissionToDelete) return;

    const { id: permissionId, version } = permissionToDelete;
    setIsLoading(true);

    dispatch(
      removeSelfServiceOrderPermission({
        id: permissionId,
        version,
      }),
    )
      .then((action) => {
        if (isFulfilled(action)) {
          const events = action.payload;
          const event = events.find((e) => e.name === "talSOPDel");

          if (event) {
            setTalentSelfServiceOrderPermissions((current) =>
              current.filter((permission) => permission.id !== permissionId),
            );
            resetDeleteState();
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onDeleteCancel = () => {
    if (isLoading) return;
    resetDeleteState();
  };

  const onClose = () => {
    setEditingPermission(null);
    reset(defaultValues);
    setDialogOpen(false);
  };

  const onSave = handleSubmit(
    async (data: TalentSelfServiceOrderPermissionForm) => {
      if (!talentId) return;

      setIsLoading(true);

      if (isEditing) {
        // Update existing permission
        const updatePayload = {
          id: editingPermission.id,
          version: editingPermission.version,
          workLocationId: data.workLocation!.id,
          customerOrganizationId: data.customerOrganization!.id,
          customerCostCenterId: data.customerCostCenter!.id,
          orderRowTemplateId: data.orderRowTemplate!.id,
          tempEmploymentReasonCode: data.reasonCode!,
          validity: {
            start: data.permissionStart
              ? format(data.permissionStart, "yyyy-MM-dd")
              : "",
            end: getValidityEndDate(data.permissionEnd, data.indefinite),
          },
        };

        dispatch(updateSelfServiceOrderPermission(updatePayload))
          .then((action) => {
            if (isFulfilled(action)) {
              const events = action.payload;
              const event = events.find((e) => e.name === "talSOPChg");

              if (event) {
                const updatedPermission: TalentSelfServiceOrderPermission = {
                  ...editingPermission,
                  id: editingPermission.id,
                  talentId: editingPermission.talentId,
                  version: event.version,
                  workLocationId: event.payload.details.workLocationId,
                  workLocationName: data.workLocation!.name,
                  customerOrganizationId:
                    event.payload.details.customerOrganizationId,
                  customerOrganizationName: data.customerOrganization!.name,
                  customerCostCenterId:
                    event.payload.details.customerCostCenterId,
                  customerCostCenterName: data.customerCostCenter!.name,
                  orderRowTemplateId: event.payload.details.orderRowTemplateId,
                  orderRowTemplateName: data.orderRowTemplate!.name,
                  reasonCode: event.payload.details
                    .reasonCode as TempEmploymentReasonCode,
                  permissionStart: event.payload.details.valid.start,
                  permissionEnd: event.payload.details.valid.end,
                  workTitleId: data.orderRowTemplate!.workTitleId,
                  tesId: editingPermission.tesId,
                };

                // Update the permission in the list
                setTalentSelfServiceOrderPermissions(
                  talentSelfServiceOrderPermissions.map((p) =>
                    p.id === editingPermission.id ? updatedPermission : p,
                  ),
                );
              }

              onClose();
            }
          })
          .finally(() => setIsLoading(false));
      } else {
        // Add new permission
        const addPayload = {
          talentId,
          workLocationId: data.workLocation!.id,
          customerOrganizationId: data.customerOrganization!.id,
          customerCostCenterId: data.customerCostCenter!.id,
          orderRowTemplateId: data.orderRowTemplate!.id,
          tempEmploymentReasonCode: data.reasonCode!,
          validity: {
            start: data.permissionStart
              ? format(data.permissionStart, "yyyy-MM-dd")
              : "",
            end: getValidityEndDate(data.permissionEnd, data.indefinite),
          },
        };

        dispatch(addSelfServiceOrderPermission(addPayload))
          .then((action) => {
            if (isFulfilled(action)) {
              const events = action.payload;
              const event = events.find((e) => e.name === "talSOPAdded");

              if (event) {
                const newPermission: TalentSelfServiceOrderPermission = {
                  id: event.id,
                  talentId: event.payload.talentId,
                  version: event.version,
                  workLocationId: event.payload.details.workLocationId,
                  workLocationName: data.workLocation!.name,
                  customerOrganizationId:
                    event.payload.details.customerOrganizationId,
                  customerOrganizationName: data.customerOrganization!.name,
                  customerCostCenterId:
                    event.payload.details.customerCostCenterId,
                  customerCostCenterName: data.customerCostCenter!.name,
                  orderRowTemplateId: event.payload.details.orderRowTemplateId,
                  orderRowTemplateName: data.orderRowTemplate!.name,
                  reasonCode: event.payload.details
                    .reasonCode as TempEmploymentReasonCode,
                  permissionStart: event.payload.details.valid.start,
                  permissionEnd: event.payload.details.valid.end,
                  workTitleId: data.orderRowTemplate!.workTitleId,
                  // tesId is not in the form or in the returned event
                  tesId: "",
                  // cannot create with inactive entities
                  isCustomerCostCenterActive: true,
                  isOrderRowTemplateActive: true,
                  isWorkLocationActive: true,
                };

                setTalentSelfServiceOrderPermissions(
                  sortPermissions([
                    ...talentSelfServiceOrderPermissions,
                    newPermission,
                  ]),
                );
              }

              onClose();
            }
          })
          .finally(() => setIsLoading(false));
      }
    },
  );

  const renderWithTooltipAndIcon = (
    value: string,
    isActive: boolean,
    key: "customerCostCenter" | "workLocation" | "orderTemplate",
  ) => {
    if (isActive) return value;

    const translatedName = t(`order:${key}`);
    const tooltipText = t("talentProfile:inActiveValueWarning", {
      key: translatedName,
    });

    return (
      <Tooltip title={tooltipText}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            cursor: "pointer",
          }}
        >
          {value}
          <InfoWarningIcon />
        </Box>
      </Tooltip>
    );
  };

  const rows = talentSelfServiceOrderPermissions.map(
    ({
      id,
      customerOrganizationName,
      workLocationName,
      orderRowTemplateName,
      customerCostCenterName,
      reasonCode,
      permissionStart,
      permissionEnd,
      isWorkLocationActive,
      isOrderRowTemplateActive,
      isCustomerCostCenterActive,
    }) => (
      <TableRow key={id}>
        <TableCell>{customerOrganizationName}</TableCell>
        <TableCell>
          {renderWithTooltipAndIcon(
            workLocationName,
            isWorkLocationActive,
            "workLocation",
          )}
        </TableCell>
        <TableCell>
          {renderWithTooltipAndIcon(
            orderRowTemplateName,
            isOrderRowTemplateActive,
            "orderTemplate",
          )}
        </TableCell>
        <TableCell>
          {renderWithTooltipAndIcon(
            customerCostCenterName,
            isCustomerCostCenterActive,
            "customerCostCenter",
          )}
        </TableCell>
        <TableCell>{t(`order:${reasonCode}`)}</TableCell>
        <TableCell>
          {format(new Date(permissionStart), leadingZerosDateFormat)}
        </TableCell>
        <TableCell>
          {isIndefiniteDate(permissionEnd)
            ? t("talentProfile:validUFN")
            : format(new Date(permissionEnd), leadingZerosDateFormat)}
        </TableCell>
        <TableCell width="120px">
          <IconButton
            id={`edit-permission-button-${id}`}
            size="small"
            onClick={() => onEdit(id)}
          >
            <IconWrapper
              icon={EditIcon}
              sx={{
                width: 28,
                height: 28,
                transform: "scale(0.57)",
                "& path": {
                  fill: "none",
                },
              }}
            />
          </IconButton>
          <IconButton
            id={`delete-permission-button-${id}`}
            size="small"
            onClick={() => onDelete(id)}
          >
            <IconWrapper
              icon={DeleteIcon}
              sx={{
                "& path": { fill: "currentColor" },
                width: 28,
                height: 28,
              }}
            />
          </IconButton>
        </TableCell>
      </TableRow>
    ),
  );

  const confirmDisabled =
    !isActiveTalent || !isDirty || !isValid || !isEmpty(errors) || isLoading;

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <StripedTable
        maxWidth="1800px"
        disableRowClick
        columnHeaders={headers}
        itemRows={rows}
        tableAction={{
          label: t("talentProfile:newSelfSignUpPermission"),
          onClick: onAdd,
        }}
      />
      <BasicDialog
        open={dialogOpen}
        title={
          editingPermission
            ? t("talentProfile:editSelfSignUpPermission")
            : t("talentProfile:newSelfSignUpPermission")
        }
        PaperProps={{ sx: { width: "697px" } }}
        onCancel={onClose}
        onConfirm={onSave}
        confirmDisabled={confirmDisabled}
        content={
          <FormProvider {...methods}>
            <OverlaySpinner isLoading={isLoading} />
            <SelfServicePermissionForm
              customerOrganization={customerOrganization}
              workLocation={workLocation}
              resetOnCustomerOrganizationChange={
                resetOnCustomerOrganizationChange
              }
              resetOnWorkLocationChange={resetOnWorkLocationChange}
              isEditing={isEditing}
              isTalentActive={isActiveTalent}
            />
          </FormProvider>
        }
      />
      <ConfirmationDialog
        title={t("talentProfile:deleteSelfSignUpPermission")}
        contentText={t("talentProfile:confirmDeleteSelfSignUpPermission")}
        cancelText={t("common:cancel")}
        confirmText={t("common:delete")}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
        isOpen={confirmationDialogOpen}
        isLoading={isLoading}
        CancelButtonProps={{ disabled: isLoading }}
        ConfirmButtonProps={{ disabled: isLoading }}
      />
    </>
  );
};

export default TalentSelfServiceOrderPermissions;
